import type { WithActiveBranchId } from '@app/api/shared';

export enum SendoutsEndpoints {
  GetSendoutById = 'GetSendoutById',
  GetSendouts = 'GetSendouts',
  PrepareSendoutCreation = 'PrepareSendoutCreation',
  CreateSendout = 'CreateSendout',
  StartSendout = 'StartSendout',
  StopSendout = 'StopSendout',
  GetSendoutFilterData = 'GetSendoutFilterData',
  PreviewSendoutMessages = 'PreviewSendoutMessages',
  GetSendoutDetails = 'GetSendoutDetails',
  GetSendoutClients = 'GetSendoutClients',
  GetSendoutFile = 'GetSendoutFile',
  GetSendoutKeywords = 'GetSendoutKeywords',
  GetSendoutForEdit = 'GetSendoutForEdit',
  EditSendout = 'EditSendout',
}

export interface SendoutsModelItem {
  Id: number;
  Name: string | null;
  IdS: string | null;
}

export interface SendoutsModelTag {
  Id: number;
  Name: string | null;
  Color: string | null;
  IconId: string | null;
  Description: string | null;
  IsDeleted: boolean;
  IsSelected: boolean;
  IsSystem: boolean;
  SystemId: string | null;
}

export interface SendoutsModelIntegrationItem {
  Id: number;
  SocialType: string | null;
  AccountLogin: string | null;
  AccountName: string | null;
  IntegrationName: string | null;
  BranchId: number;
  Branch: string | null;
}

export interface SendoutsModelFile {
  Id: number;
  Name: string | null;
  Data: string | null;
  Lifetime: string | null;
  Description: string | null;
}

export interface SendoutsModelFileDescription {
  Id: number;
  FileName: string | null;
  FileSize: number;
  Description: string | null;
  Extension: string | null;
  Thumbnail: string | null;
}

export interface ModelCreateSendout {
  Name: string | null;
  Message: string | null;
  ChannelId: string | null;
  IntegrationId: number | null;
  SendoutDelaySec: number;
  TagIds: number[] | null;
  ClientIds: number[] | null;
  clientsSearchId: number | null;
  leadsSearchId: number | null;
  Attachments: SendoutsModelFile[] | null;
  Timezone: string | null;
  MoS: string | null;
  MoE: string | null;
  TuS: string | null;
  TuE: string | null;
  WeS: string | null;
  WeE: string | null;
  ThS: string | null;
  ThE: string | null;
  FrS: string | null;
  FrE: string | null;
  SaS: string | null;
  SaE: string | null;
  SuS: string | null;
  SuE: string | null;
  IsNoSchedule: boolean;
}

export interface ModelEditSendout {
  SendoutId: number;
  Name: string | null;
  Message: string | null;
  SendoutDelaySec: number;
  ReadAttachments: SendoutsModelFileDescription[] | null;
  ReadTags: SendoutsModelTag[] | null;
  TagIds: number[] | null;
  Attachments: SendoutsModelFile[] | null;
  Timezone: string | null;
  MoS: string | null;
  MoE: string | null;
  TuS: string | null;
  TuE: string | null;
  WeS: string | null;
  WeE: string | null;
  ThS: string | null;
  ThE: string | null;
  FrS: string | null;
  FrE: string | null;
  SaS: string | null;
  SaE: string | null;
  SuS: string | null;
  SuE: string | null;
  IsNoSchedule: boolean;
}

export interface ModelPrepareSendoutCreation {
  Channels: SendoutsModelItem[] | null;
  Integrations: SendoutsModelIntegrationItem[] | null;
  Tags: SendoutsModelTag[] | null;
}

export interface ModelPreviewSendoutMessages {
  Message: string | null;
  Count: number | null;
  ClientIds: number[] | null;
}

export interface ModelSendout {
  Id: number;
  TimeCreated: string;
  TimeStart: string | null;
  TimeCompleted: string | null;
  Status: string | null;
  ErrorMessage: string | null;
  Name: string | null;
  Message: string | null;
  CountReceivers: number;
  CountSentSuccess: number;
  CountSentFailure: number;
  ChannelId: string | null;
  Channel: string | null;
  IntegrationId: number | null;
  Integration: string | null;
  SendoutDelaySec: number;
  IsAttachments: boolean;
  CountClientReplies: number;
  CountClientRecords: number;
  SendoutConversion: number;
  ClientRecordsCost: number;
  ApproximateFinish: string | null;
}

export interface ModelSendoutFilter {
  SearchTerm: string | null;
  ChannelIds: string[] | null;
  IntegrationIds: number[] | null;
  StatusIds: string[] | null;
  ItemsPerPage: number;
  PageN: number;
}

export interface ModelSendoutsList {
  PageN: number;
  ItemsPerPage: number;
  TotalPages: number;
  TotalItems: number;
  Filter: ModelSendoutFilter;
  Data: ModelSendout[] | null;
}

export interface ModelSendoutFilterData {
  Channels: SendoutsModelItem[] | null;
  Integrations: SendoutsModelItem[] | null;
  Statuses: SendoutsModelItem[] | null;
}

export interface ModelSendoutDetails {
  Sendout: ModelSendout;
  Attachments: SendoutsModelFileDescription[] | null;
  Tags: SendoutsModelTag[] | null;
  Timezone: string | null;
  MoS: string | null;
  MoE: string | null;
  TuS: string | null;
  TuE: string | null;
  WeS: string | null;
  WeE: string | null;
  ThS: string | null;
  ThE: string | null;
  FrS: string | null;
  FrE: string | null;
  SaS: string | null;
  SaE: string | null;
  SuS: string | null;
  SuE: string | null;
}

export interface ModelSendoutClient {
  Id: number;
  LeadId: number;
  Name: string | null;
  Phone1: string | null;
  Phone2: string | null;
  Instagram: string | null;
  TimeSent: string | null;
  ChannelId: string | null;
  IntegrationId: number | null;
  Integration: string | null;
  Status: string | null;
  ErrorMessage: string | null;
  IsClientReplied: boolean;
  IsClientRecorded: boolean;
  ClientRecordsCost: number;
  UsernameVk: string | null;
  UsernameInstagram: string | null;
  UsernameTelegramBot: string | null;
  UsernameTelegram: string | null;
  UsernameMax: string | null;
  UsernameLoyalty: string | null;
}

export interface ModelSendoutClientsFilter {
  SendoutId: number;
  SearchTerm: string | null;
  StatusIds: string[] | null;
  ActionIds: string[] | null;
  ItemsPerPage: number;
  PageN: number;
}

export interface ModelSendoutClientsList {
  SendoutId: number;
  Sendout: string | null;
  PageN: number;
  ItemsPerPage: number;
  TotalPages: number;
  TotalItems: number;
  Filter: ModelSendoutClientsFilter;
  Data: ModelSendoutClient[] | null;
}

export interface ModelKeywordDescription {
  Keyword: string | null;
  Description: string | null;
}

export type GetSendoutByIdRequest = WithActiveBranchId<{
  sendoutId: number;
}>;
export type GetSendoutsRequest = WithActiveBranchId<{
  SearchTerm?: string;
  ChannelIds?: string[];
  IntegrationIds?: number[];
  StatusIds?: string[];
  ItemsPerPage?: number;
  PageN?: number;
}>;
export type PrepareSendoutCreationRequest = WithActiveBranchId<{
  channelId?: string;
  integrationId?: number;
  tagIds?: number[];
}>;
export type CreateSendoutRequest = WithActiveBranchId<{
  data: ModelCreateSendout;
}>;
export type StartSendoutRequest = WithActiveBranchId<{
  sendoutId: number;
}>;
export type StopSendoutRequest = WithActiveBranchId<{
  sendoutId: number;
}>;
export type GetSendoutFilterDataRequest = WithActiveBranchId;
export type PreviewSendoutMessagesRequest = WithActiveBranchId<{
  data: ModelPreviewSendoutMessages;
}>;
export type GetSendoutDetailsRequest = WithActiveBranchId<{
  sendoutId: number;
}>;
export type GetSendoutClientsRequest = WithActiveBranchId<{
  SendoutId: number;
  SearchTerm?: string;
  StatusIds?: string[];
  ActionIds?: string[];
  ItemsPerPage?: number;
  PageN?: number;
}>;
export type GetSendoutFileRequest = WithActiveBranchId<{
  fileId: number;
}>;
export type GetSendoutKeywordsRequest = WithActiveBranchId;
export type GetSendoutForEditRequest = WithActiveBranchId<{
  sendoutId: number;
}>;
export type EditSendoutRequest = WithActiveBranchId<{
  data: ModelEditSendout;
}>;

export type GetSendoutByIdResponse = ModelSendout;
export type GetSendoutsResponse = ModelSendoutsList;
export type PrepareSendoutCreationResponse = ModelPrepareSendoutCreation;
export type CreateSendoutResponse = ModelSendout;
export type StartSendoutResponse = ModelSendout;
export type StopSendoutResponse = ModelSendout;
export type GetSendoutFilterDataResponse = ModelSendoutFilterData;
export type PreviewSendoutMessagesResponse = string[];
export type GetSendoutDetailsResponse = ModelSendoutDetails;
export type GetSendoutClientsResponse = ModelSendoutClientsList;
export type GetSendoutFileResponse = SendoutsModelFile;
export type GetSendoutKeywordsResponse = ModelKeywordDescription[];
export type GetSendoutForEditResponse = ModelEditSendout;
export type EditSendoutResponse = ModelSendout;
